import { Component, Input, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { TranslateService } from "@root/shared/service/translate.service";
import { NzModalService } from "ng-zorro-antd/modal";
import { Observable, delay, filter, forkJoin, mergeMap, switchMap, tap } from "rxjs";
import { Router } from "@angular/router";
import _ from "lodash";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { OversaleCalendarComponent } from "@root/router/component/calendar-picker/oversale-calendar/oversale-calendar.component";
import { WillDeprecated_StopHouseCalendarComponent } from "@root/router/component/calendar-picker/stop-house-calendar/stop-house-calendar.component";
import moment from "moment";
import { PropertyDetailService } from "../../property-detail.service";
import { V4HousesModalSelectComponent } from "@root/router/component/v4/houses-modal-select/houses-modal-select.component";
import { HttpClient } from "@angular/common/http";
import { NzMessageService } from "ng-zorro-antd/message";
import { AppService } from "@root/app.service";
interface DateItem {
  from: string;
  to: string;
  thirdparty_type?: number;
  dates?: string[];
}

interface SubHouseItem {
  id: number;
  title_alias: string;
}

@Component({
  selector: "property-detail-advanced",
  templateUrl: "./advanced.component.html",
  styleUrls: ["./advanced.component.scss"],
})
export class AdvancedComponent implements OnInit {
  _houseId;
  @Input()
  get houseId() {
    return this._houseId;
  }
  set houseId(id) {
    this._houseId = id;
    if (id) {
      this.fetchSettingDetail();
      this.fetchStopDetail();
    }
  }

  @Input()
  set timezone(value) {
    this.form.patchValue({ timezone: value }, { emitEvent: false });
  }

  get isInternational() {
    return this.app.app.international;
  }

  form: UntypedFormGroup = this.fb.group({
    is_allowed_overbooking: false,
    is_stop_managing_inventory: false,
    is_link_sub_house: false,

    // 下面四个字段属于接口设计失误，但由于历史原因，只得保留
    dates: [[]],
    date_list: [[]],
    thirdparty_dates: [[]],
    thirdparty_date_list: [[]],

    // 下面的字段是另一个接口
    stop_status: false,
    stop_dates: [[]],

    // timezone
    timezone: null,
  });
  isSubHouse = false;
  masterHouse: SubHouseItem; // 只有子房间才有该字段
  oversaleDateItems: DateItem[] = []; // 展示在允许超售下面的日期，和form.dates不同，这里是处理过的展示数据，form.dates是原始数据
  subHouseList: SubHouseItem[] = []; // 该字段仅用于展示，不作为参数使用
  stopDateItems: DateItem[] = []; // 用于展示的停用房日期，对应form里的stop_dates

  optionsOfTimezone: Option[] = [];

  constructor(
    private service: PropertyDetailService,
    private http: HttpClient,
    private t: TranslateService,
    private modal: NzModalService,
    private msg: NzMessageService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private app: AppService,
  ) {}

  ngOnInit() {
    this.fetchOptionsOfTimezone();
    this.subscribeFormChange();
  }

  private subscribeFormChange() {
    this.form.get("is_allowed_overbooking").valueChanges.subscribe((checked) => {
      if (checked) {
        if (!this.oversaleDateItems.length) {
          return this.displayAddDatesModal();
        }
      } else {
        // todo 这里可以将四个date_list字段置空
        this.form.patchValue({
          dates: [],
          date_list: [],
          thirdparty_dates: [],
          thirdparty_date_list: [],
        });
        this.requestSaveSetting();
      }
    });

    // delay的原因：表单字段的变化先于整个表单的变化触发，此时取到的整个表单的值是变化之前的值
    // 只有手动操作html组件才会触发这些逻辑。通过操作数据更改表单字段，会使用emitEvent=false来避免触发。因为两者的逻辑存在差别
    this.form
      .get("is_stop_managing_inventory")
      .valueChanges.pipe(delay(10))
      .subscribe((checked) => {
        console.log("is stop managing inventory change: ", checked);

        this.requestSaveSetting();
      });

    this.form
      .get("is_link_sub_house")
      .valueChanges.pipe(delay(10))
      .subscribe((checked) => {
        console.log("link sub house checked: ", checked);
        /**
         * 分 2 种情况：
         * 1，打开，并且没有子房间，此时弹窗让用户添加子房间
         * 2，打开或者关闭，此时提醒用户是否应用到所有订单
         */
        if (checked && !this.subHouseList.length) {
          return this.displayAddChildPropertiesModal();
        } else {
          this.displayApplyToResvsWarning().subscribe((status) => {
            this.setSubHousesAndUpdateSetting(status === "ok");
          });
        }
      });

    this.form
      .get("stop_status")
      .valueChanges.pipe(delay(10))
      .subscribe((checked) => {
        if (checked) {
          if (this.stopDateItems.length === 0) {
            return this.displayAddStopDatesModal();
          }
        } else {
          this.form.patchValue({ stop_dates: [] });
          this.requestSaveStopInfo();
        }
      });

    this.form
      .get("timezone")
      .valueChanges.pipe(delay(10))
      .subscribe((rst) => {
        this.requestUpdateTimezone();
      });
  }

  private fetchOptionsOfTimezone() {
    this.http.get("api/international/list").subscribe((rst: any) => {
      this.optionsOfTimezone = rst?.data?.timezone_list;
    });
  }

  private fetchSettingDetail() {
    this.service
      .getOverbookingDetails({
        link_id: this.houseId,
        link_type: "house",
      })
      .subscribe((res) => {
        if (res.error_code === 0) {
          const data = res.data || {};
          const {
            is_allowed_overbooking,
            is_stop_managing_inventory,
            is_link_sub_house,
            is_sub_house,
            master_house,
            sub_house_list,
            date_list,
            dates,
            thirdparty_date_list,
            thirdparty_dates,
          } = data;

          this.isSubHouse = is_sub_house;
          this.masterHouse = master_house;
          this.oversaleDateItems = this.processOversaleDateItemsCompat(
            dates,
            date_list,
            thirdparty_dates,
            thirdparty_date_list,
          );
          this.subHouseList = sub_house_list;
          console.log("this.overslaeDateItem: ", this.oversaleDateItems);

          this.form.patchValue(
            {
              ...data,
              is_allowed_overbooking: !!is_allowed_overbooking,
              is_stop_managing_inventory: !!is_stop_managing_inventory,
              is_link_sub_house: !!is_link_sub_house,
            },
            { emitEvent: false },
          );
        }
      });
  }

  private fetchStopDetail() {
    this.service.getStopDetail({ house_id: this.houseId }).subscribe((rst) => {
      if (rst.error_code === 0) {
        const record = rst.data.record || {};
        const { stop_status, dates } = record;

        this.form.patchValue(
          {
            stop_status: !!stop_status,
            stop_dates: dates,
          },
          { emitEvent: false },
        );

        this.stopDateItems = this.processStopDateItems(dates);
        // this.stopDates = dates;
      }
    });
  }

  private requestUpdateTimezone() {
    const params = {
      timezone: this.form.value.timezone,
      house_id: this.houseId,
    };
    this.http.post("/api/house/update_timezone", params).subscribe((rst: any) => {
      if (rst.error_code === 0) {
        this.msg.success(this.t.t("success.save"));
      }
    });
  }

  private requestSaveSetting() {
    const value = this.form.value;
    console.log("this.form.value: ", value);
    const params = {
      link_id: this.houseId,
      link_type: "house",
      ...value,
      is_allowed_overbooking: value.is_allowed_overbooking & 1,
      is_stop_managing_inventory: value.is_stop_managing_inventory & 1,
    };
    this.service.setOverbooking(params).subscribe((rst) => {
      if (rst.error_code === 0) {
        this.fetchSettingDetail();
      }
    });
  }

  private requestSaveStopInfo() {
    const { stop_status, stop_dates } = this.form.value;
    const params = {
      house_id: this.houseId,
      stop_status: stop_status & 1,
      dates: stop_dates,
    };
    this.service.updateStopStatus(params).subscribe((rst) => {
      if (rst.error_code === 0) {
        this.fetchStopDetail();
      }
    });
  }

  /** @deprecated */
  private requestSetSubHouse() {
    const params = {
      master_house_id: this.houseId,
      house_ids: this.subHouseList.map((house) => house.id),
    };
    return new Promise((resolve, reject) => {
      this.service.setSubHouse(params).subscribe((rst) => {
        if (rst.error_code === 0) {
          resolve(null);
        }
      });
    });
  }

  private requestSetSubHouseV2(extend = false) {
    const params = {
      master_house_id: this.houseId,
      house_ids: this.subHouseList.map((house) => house.id),
      status: this.form.get("is_link_sub_house").value ? 1 : 0,
      extend_exist_reservation: extend ? 1 : 0,
    };

    this.service.requestSetSubHouseV2(params).subscribe((rst: any) => {
      if (rst.error_code === 0) {
        this.msg.success(this.t.t("success.save"));
      }
    });
  }

  // 兼容处理oversale的日期展示
  // 由于接口设计失误，一个字段可以解决的事情弄成了四个字段，因此这里将四个字段收敛为一个
  private processOversaleDateItemsCompat(
    dates: string[],
    dateList: DateItem[],
    thirdpartyDates: { date: string; thirdparty_type: number }[],
    thirdpartyDateList: DateItem[],
  ) {
    const _dates: DateItem[] = dates.map((date) => ({ from: date, to: date }));
    const _thirdpartyDates: DateItem[] = thirdpartyDates.map((date) => ({
      from: date.date,
      to: date.date,
      thirdparty_type: date.thirdparty_type,
    }));

    return [..._dates, ...dateList, ..._thirdpartyDates, ...thirdpartyDateList];
  }

  // 处理stop的日期展示
  // 将发散的日期处理成from,to的形式
  private processStopDateItems(dates: string[]) {
    // todo 可以排一下序
    // 过滤重复数据
    const list = Array.from(new Set(dates));

    const isContinuousDates = (left, right) => {
      return moment(right).diff(moment(left), "day") === 1;
    };
    // 对连续的日期进行分组
    // ['2012-11-11', '2012-11-12', '2012-11-13', '2012-11-15'] =>
    // [{ from: '2012-11-11', to: '2011-11-13', dates: [...] }, ...]
    return list.reduce((total, current) => {
      const prevIndex = total.length - 1;
      const prev = total[prevIndex];

      if (!prev) {
        total.push({ from: current, to: current, dates: [current] });
      } else {
        if (isContinuousDates(prev.to, current)) {
          prev.to = current;
          prev.dates.push(current);
        } else {
          total.push({ from: current, to: current, dates: [current] });
        }
      }
      return total;
    }, []);
  }

  // 几种情况
  // 1, 当前超售列表为空，modal返回null
  // 2，当前超售列表为空，modal返回list
  // 3, 当前超售列表不为空，modal返回null
  // 4, 当前超售列表不为空，modal返回list
  private displayAddDatesModal() {
    const modal = this.modal.create({
      nzTitle: this.t.t("house.set_overbooking_date"),
      // nzContent: this.setOverSoldDate,
      nzContent: OversaleCalendarComponent,
      nzFooter: null,
      nzWidth: 750,
    });
    modal.afterClose.subscribe((rst) => {
      // modal返回为空的情况
      if (!rst) {
        // 当前超售列表为空的情况。不为空的情况无需处理
        if (this.oversaleDateItems.length === 0) {
          this.form.patchValue({ is_allowed_overbooking: false }, { emitEvent: false });
          // this.requestSaveSetting();
          return;
        }
      }

      // modal返回不为空的情况
      const { dates, channels } = rst;
      if (channels.length) {
        const _dates = [].concat(
          ...dates.map((date) => {
            return channels.map((channel) => ({ date, thirdparty_type: channel }));
          }),
        );
        const _thirdpartyDates = this.form.get("thirdparty_dates").value;
        this.form.patchValue({ thirdparty_dates: [].concat(_dates, _thirdpartyDates) });
      } else {
        const _dates = this.form.get("dates").value;
        this.form.patchValue({ dates: [].concat(_dates, dates) });
      }
      // 处理后数据为空，则要置空。（理论上不会为空，modal会做空校验，这里为了保险，加上校验）
      // if (this.oversaleDateItems.length === 0) {
      //   this.form.patchValue({ is_allowed_overbooking: false }, { emitEvent: false });
      // }

      this.requestSaveSetting();
    });
  }

  //#region 整租分租联动
  private displayAddChildPropertiesModal() {
    const modal = this.modal.create({
      nzTitle: this.t.t("modal.property_detail.control.add_house_title"),
      nzContent: V4HousesModalSelectComponent,
      nzWidth: 725,
      nzFooter: null,
      nzComponentParams: {
        params: {
          master_house_id: this.houseId,
        },
      },
    });

    modal.afterClose
      .pipe(
        filter(({ status }) => status === "ok"),
        tap(({ data }) => {
          const list: SubHouseItem[] = data?.map((house) => ({
            id: house.value,
            title_alias: house.label,
          }));
          this.subHouseList = _.uniqBy((this.subHouseList || []).concat(list), "id");
        }),
        mergeMap(() => this.displayApplyToResvsWarning()),
      )
      .subscribe((status) => {
        this.setSubHousesAndUpdateSetting(status === "ok");
      });

    // closeModal$.subscribe(({ data }) => {
    //   addPropertiesWarn$.subscribe(() => {
    //     if (data) {
    //       const list: SubHouseItem[] = data.map((house) => ({
    //         id: house.value,
    //         title_alias: house.label,
    //       }));
    //       this.subHouseList = _.uniqBy((this.subHouseList || []).concat(list), "id");
    //       this.setSubHousesAndUpdateSetting();
    //     }
    //   });
    // });
  }

  private displayApplyToResvsWarning(): Observable<"ok" | "cancel"> {
    return new Observable((observer) => {
      const modal = this.modal.confirm({
        nzTitle: this.t.t("apply-to-reservations_modal_title"),
        nzContent: this.t.t("apply-to-reservations_modal_content"),
        nzWidth: 540,
        nzCancelText: this.t.t("word.no"),
        nzOkText: this.t.t("word.yes"),
        nzOnOk: () => {
          observer.next("ok");
        },
        nzOnCancel: () => {
          observer.next("cancel");
        },
      });

      modal.afterClose.subscribe(() => observer.complete());
    });
  }

  //#endregion

  private displayAddStopDatesModal() {
    const modal = this.modal.create({
      nzTitle: this.t.t("property.property_detail.status_setting.stop_modal.title"),
      // nzContent: this.setOverSoldDate,
      nzContent: WillDeprecated_StopHouseCalendarComponent,
      nzFooter: null,
      nzWidth: 750,
    });

    modal.afterClose.subscribe((rst) => {
      if (!rst) {
        if (this.stopDateItems.length === 0) {
          this.form.patchValue({ stop_status: false }, { emitEvent: false });
          return;
        }
      }

      const dates = rst.dates;
      const stopDates = this.form.get("stop_dates").value;
      this.form.patchValue({ stop_dates: [].concat(dates, stopDates) });
      this.requestSaveStopInfo();
    });
  }

  onClickAddHouse() {
    this.displayAddChildPropertiesModal();
  }

  onClickAddDateItem() {
    this.displayAddDatesModal();
  }

  onClickAddStopDates() {
    this.displayAddStopDatesModal();
  }

  onDeleteHouseItem(id) {
    _.remove(this.subHouseList, (house) => house.id === id);

    this.displayApplyToResvsWarning().subscribe((status) => {
      this.setSubHousesAndUpdateSetting(status === "ok");
    });
    // this.requestSetSubHouse();

    // if (this.subHouseList.length === 0) {
    //   this.form.patchValue({ is_link_sub_house: false }, { emitEvent: false});
    //   this.requestSaveSetting();
    // }
  }

  onDeleteDateItem(item: DateItem, index: number): void {
    // 删除item
    this.oversaleDateItems.splice(index, 1);
    // 更新原始数据
    const deleteFormDateItem = (name, cb) => {
      const origin = this.form.value[name];
      _.remove(origin, cb);
      this.form.patchValue({ [name]: origin });
    };

    if (item.thirdparty_type === undefined) {
      console.log("Not channel date");

      if (item.from === item.to) {
        deleteFormDateItem("dates", (date) => date === item.from);
      } else {
        deleteFormDateItem("date_list", (date) => date.from === item.from && date.to === item.to);
      }
    } else {
      console.log("channel date");
      if (item.from === item.to) {
        deleteFormDateItem(
          "thirdparty_dates",
          (date) => date.date === item.from && date.thirdparty_type === item.thirdparty_type,
        );
      } else {
        deleteFormDateItem(
          "thirdparty_date_list",
          (date) => date.from === item.from && date.to === item.to && date.thirdparty_type === item.thirdparty_type,
        );
      }
    }
    // 全部删除后，需要将该选项置为false
    if (this.oversaleDateItems.length === 0) {
      this.form.patchValue({ is_allowed_overbooking: false }, { emitEvent: false });
    }

    this.requestSaveSetting();
  }

  onDeleteStopDateItem(item: DateItem, index: number) {
    const dates = item.dates;
    // 删除展示列表中的item
    this.stopDateItems.splice(index, 1);
    // 删除并更新form中对应选择的日期
    const stopDates = this.form.get("stop_dates").value;
    _.remove(stopDates, (date: string) => dates.indexOf(date) !== -1);
    this.form.patchValue({ stop_dates: stopDates });
    // 如果日期个数为零，则将选项置为false
    if (stopDates.length === 0) {
      this.form.patchValue({ stop_status: false }, { emitEvent: false });
    }
    this.requestSaveStopInfo();
  }

  private setSubHousesAndUpdateSetting(extend = false) {
    // 子房间为0时，需要将选项关闭
    if (!this.subHouseList.length) {
      this.form.patchValue({ is_link_sub_house: false }, { emitEvent: false });
    }

    this.requestSetSubHouseV2(extend);
    // 先保存子房间，然后保存选项
    // this.requestSetSubHouse().then(() => {
    //   // todo 并不是每次都需要更新配置，只有在is_link_sub_house改变的时候才需要
    //   this.requestSaveSetting();
    // });
  }

  toMasterHouse() {
    this.router.navigate(["/app/properties/detail"], {
      queryParams: {
        id: this.masterHouse.id,
      },
    });
  }
}
